import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import TeamPreviewField from "../components/TeamPreviewField";
import { CRICKET_API_URL } from "../config";
import { getMatchStatus, getTeamScore } from "../utils/matchHelpers";

const DEFAULT_TEAM_IMAGE = "https://h.cricapi.com/img/icon512.png";

const getStoredUserId = () => {
    try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            const userObj = JSON.parse(userStr);
            return userObj?._id || userObj?.id || null;
        }
    } catch (error) {
        console.error("Parse error");
    }

    return localStorage.getItem("userId") || localStorage.getItem("_id") || null;
};

const getTeamCode = (teamName = "T1") =>
    String(teamName || "T1")
        .substring(0, 3)
        .toUpperCase();

const normalizePoints = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatPointsDisplay = (value) => {
    const normalizedPoints = normalizePoints(value);
    if (normalizedPoints === null) return "-";

    return Number.isInteger(normalizedPoints)
        ? normalizedPoints
        : Number(normalizedPoints.toFixed(1));
};

const normalizePlayerName = (value = "") =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

const isSamePlayer = (fullName, shortName) => {
    if (!fullName || !shortName) return false;

    const full = normalizePlayerName(fullName);
    const short = normalizePlayerName(shortName);

    if (full === short) return true;

    const fullParts = full.split(" ").filter(Boolean);
    const shortParts = short.split(" ").filter(Boolean);

    if (!fullParts.length || !shortParts.length) return false;

    const fullLastName = fullParts[fullParts.length - 1];
    const shortLastName = shortParts[shortParts.length - 1];

    return Boolean(
        fullLastName === shortLastName &&
        fullParts[0]?.[0] &&
        shortParts[0]?.[0] &&
        fullParts[0][0] === shortParts[0][0],
    );
};

const normalizePlayerIdentity = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const buildPlayerLookupKey = (type, value) => {
    const normalizedValue = type === "name"
        ? normalizePlayerName(value)
        : normalizePlayerIdentity(value);

    return normalizedValue ? `${type}:${normalizedValue}` : "";
};

const getPlayerNameCandidate = (player = {}) =>
    player?.name ||
    player?.playerName ||
    player?.player_name ||
    player?.player ||
    player?.fullName ||
    player?.full_name ||
    player?.event_player_name ||
    "";

const getPlayerIdCandidates = (player = {}) =>
    [
        player?.id,
        player?.playerId,
        player?.player_id,
        player?.pid,
        player?._id,
        player?.key,
        player?.event_player_key,
    ]
        .map(normalizePlayerIdentity)
        .filter(Boolean);

const sortLeaderboardEntries = (entries = []) =>
    [...(entries || [])].sort((a, b) => {
        const pointsA = normalizePoints(a?.points) ?? -1;
        const pointsB = normalizePoints(b?.points) ?? -1;
        return pointsB - pointsA;
    });

const buildLivePlayerPointsLookup = (playerPoints = []) => {
    const playerPointsLookup = new Map();

    (playerPoints || []).forEach((playerPoint) => {
        const playerNameKey = buildPlayerLookupKey(
            "name",
            getPlayerNameCandidate(playerPoint),
        );
        const points = normalizePoints(
            playerPoint?.points ??
            playerPoint?.fantasyPoints ??
            playerPoint?.totalPoints,
        );

        if (points === null) return;

        if (playerNameKey) {
            playerPointsLookup.set(playerNameKey, points);
        }

        getPlayerIdCandidates(playerPoint).forEach((playerId) => {
            playerPointsLookup.set(buildPlayerLookupKey("id", playerId), points);
        });
    });

    return playerPointsLookup;
};

const getCommentaryOrderValue = (over = "") => {
    const [overPart = "0", ballPart = "0"] = String(over || "").split(".");
    const parsedOver = Number.parseInt(overPart, 10);
    const parsedBall = Number.parseInt(ballPart, 10);

    if (Number.isNaN(parsedOver) && Number.isNaN(parsedBall)) {
        return -1;
    }

    return (Number.isNaN(parsedOver) ? 0 : parsedOver * 10) + (Number.isNaN(parsedBall) ? 0 : parsedBall);
};

const normalizeCommentaryRuns = (value) => {
    if (value === null || value === undefined) return null;

    const normalizedValue = String(value).trim();
    return normalizedValue === "" ? null : normalizedValue;
};

const getCommentaryRunsMeta = (runs) => {
    const normalizedRuns = normalizeCommentaryRuns(runs);

    if (!normalizedRuns) return null;

    const loweredRuns = normalizedRuns.toLowerCase();

    if (normalizedRuns === "4") {
        return { label: "FOUR!", className: "text-success" };
    }

    if (normalizedRuns === "6") {
        return { label: "SIX!", className: "text-success" };
    }

    if (loweredRuns.includes("w")) {
        return { label: "WICKET!", className: "text-danger" };
    }

    return { label: `${normalizedRuns} Run(s)`, className: "text-muted" };
};

const normalizeCommentaryItems = (items = []) =>
    [...(items || [])]
        .map((item, index) => {
            const runs = normalizeCommentaryRuns(item?.runs ?? item?.run);

            return {
                id: `${String(item?.over || "over").trim()}-${index}`,
                over: String(item?.over || "").trim(),
                text: String(item?.text || item?.post || "").trim(),
                runs,
            };
        })
        .filter((item) => item.over || item.text || item.runs !== null)
        .sort((a, b) => getCommentaryOrderValue(b.over) - getCommentaryOrderValue(a.over));

const shouldShowLeaderboardWinnings = ({ status = "", settled = false } = {}) => {
    if (Boolean(settled)) return true;

    const normalizedStatus = String(status || "").trim().toLowerCase();

    return (
        normalizedStatus === "completed" ||
        normalizedStatus === "settled" ||
        normalizedStatus.includes("completed") ||
        normalizedStatus.includes("finished") ||
        normalizedStatus.includes("result")
    );
};

const formatPrizeRank = (prize = {}) => {
    const rankStart = prize.rankStart ?? prize.rank;
    const rankEnd = prize.rankEnd ?? prize.rank;

    if (!rankStart && !rankEnd) return "Rank";
    if (rankStart === rankEnd || !rankEnd) return `#${rankStart}`;
    return `#${rankStart} - #${rankEnd}`;
};

const getResolvedTeamId = (user = {}) =>
    user?.userTeamId?._id ||
    user?.userTeamId?.id ||
    user?.userTeamId ||
    null;

const matchesPlayerId = (player = {}, targetId) => {
    const normalizedTargetId = normalizePlayerIdentity(targetId);

    return Boolean(
        normalizedTargetId &&
        getPlayerIdCandidates(player).includes(normalizedTargetId),
    );
};

const matchesPlayerSelection = (player = {}, targetId) => {
    if (matchesPlayerId(player, targetId)) return true;

    const targetNameKey = normalizePlayerName(targetId);
    const playerNameKey = normalizePlayerName(getPlayerNameCandidate(player));

    return Boolean(targetNameKey && playerNameKey && targetNameKey === playerNameKey);
};

const getLivePlayerPointsLookup = (livePlayerPoints = []) =>
    livePlayerPoints instanceof Map
        ? livePlayerPoints
        : buildLivePlayerPointsLookup(livePlayerPoints);

const getResolvedPlayerPoints = (player = {}, playerPointsLookup = new Map()) => {
    const playerNameKey = buildPlayerLookupKey(
        "name",
        getPlayerNameCandidate(player),
    );

    if (playerNameKey && playerPointsLookup.has(playerNameKey)) {
        return playerPointsLookup.get(playerNameKey);
    }

    const matchedPlayerId = getPlayerIdCandidates(player).find((playerId) =>
        playerPointsLookup.has(buildPlayerLookupKey("id", playerId)),
    );

    if (matchedPlayerId) {
        return playerPointsLookup.get(buildPlayerLookupKey("id", matchedPlayerId));
    }

    const playerFullName = getPlayerNameCandidate(player);
    const matchedShortNameEntry = Array.from(playerPointsLookup.entries()).find(
        ([lookupKey]) =>
            lookupKey.startsWith("name:") &&
            isSamePlayer(playerFullName, lookupKey.replace(/^name:/, "")),
    );

    if (matchedShortNameEntry) {
        return matchedShortNameEntry[1];
    }

    if (playerPointsLookup.size > 0) return 0;

    return normalizePoints(player?.points) ?? 0;
};

const getCalculatedPlayerPoints = (
    player = {},
    livePlayerPoints = [],
    captainId = null,
    viceCaptainId = null,
) => {
    const playerPointsLookup = getLivePlayerPointsLookup(livePlayerPoints);
    let actualPoints = getResolvedPlayerPoints(player, playerPointsLookup);

    if (matchesPlayerSelection(player, captainId)) actualPoints *= 2;
    if (matchesPlayerSelection(player, viceCaptainId)) actualPoints *= 1.5;

    return actualPoints;
};

const calculateTeamPoints = (
    userTeamPlayers = [],
    livePlayerPoints = [],
    captainId = null,
    viceCaptainId = null,
) =>
    (userTeamPlayers || []).reduce(
        (sum, player) =>
            sum + getCalculatedPlayerPoints(
                player,
                livePlayerPoints,
                captainId,
                viceCaptainId,
            ),
        0,
    );

const getResolvedTeamTotalPoints = (team = {}, playerPointsLookup = new Map()) =>
    calculateTeamPoints(
        team?.players || [],
        playerPointsLookup,
        team?.captainId,
        team?.viceCaptainId || team?.vcId,
    );

const rankLeaderboardEntries = (entries = []) => {
    const sortedEntries = sortLeaderboardEntries(entries);
    let currentRank = 1;

    return sortedEntries.map((entry, index) => {
        const previousEntry = sortedEntries[index - 1];
        const samePointsAsPrevious =
            index > 0 &&
            normalizePoints(entry?.points) === normalizePoints(previousEntry?.points);
        const rank = samePointsAsPrevious ? previousEntry.rank : currentRank;

        currentRank += 1;

        return {
            ...entry,
            rank,
        };
    });
};

const getPlayerRoleGroupKey = (role = "") => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    if (
        normalizedRole.includes("wk") ||
        normalizedRole.includes("keeper")
    ) {
        return "wicketKeepers";
    }

    if (
        normalizedRole.includes("all") ||
        normalizedRole.includes("ar")
    ) {
        return "allRounders";
    }

    if (normalizedRole.includes("bowl")) {
        return "bowlers";
    }

    if (normalizedRole.includes("bat")) {
        return "batters";
    }

    return "batters";
};

const LiveLeaderboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { contestId: routeContestId } = useParams();

    const [contestData, setContestData] = useState(location.state?.contest || null);
    const [matchInfo, setMatchInfo] = useState(location.state?.matchData || {});
    const actualContestId = routeContestId || contestData?._id || contestData?.id;
    const actualMatchId =
        contestData?.matchId ||
        matchInfo?.matchId ||
        matchInfo?._id ||
        matchInfo?.id;
    const currentUserId = useMemo(() => getStoredUserId(), []);
    const token = localStorage.getItem("token");
    const headers = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token],
    );

    const [activeTab, setActiveTab] = useState("leaderboard");
    const [loading, setLoading] = useState(true);
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [liveScoreInfo, setLiveScoreInfo] = useState(null);
    const [commentaryData, setCommentaryData] = useState([]);
    const [selectedOpponent, setSelectedOpponent] = useState(null);
    const [opponentTeamData, setOpponentTeamData] = useState(null);
    const [opponentLoading, setOpponentLoading] = useState(false);
    const livePlayerPointsLookup = useMemo(
        () => buildLivePlayerPointsLookup(liveScoreInfo?.playerPoints || []),
        [liveScoreInfo?.playerPoints],
    );
    const isLiveMatch = useMemo(
        () =>
            getMatchStatus({
                ...matchInfo,
                status: liveScoreInfo?.status || matchInfo?.status || "",
                apiStatus:
                    liveScoreInfo?.apiStatus ||
                    matchInfo?.apiStatus ||
                    liveScoreInfo?.event_status ||
                    "",
                status_info:
                    liveScoreInfo?.status_info ||
                    liveScoreInfo?.event_status_info ||
                    matchInfo?.status_info ||
                    matchInfo?.event_status_info ||
                    "",
                toss:
                    liveScoreInfo?.toss ||
                    liveScoreInfo?.event_toss ||
                    matchInfo?.toss ||
                    matchInfo?.event_toss ||
                    "",
                isLive:
                    liveScoreInfo?.isLive === true ||
                    matchInfo?.isLive === true ||
                    String(liveScoreInfo?.event_live || matchInfo?.event_live || "").trim() === "1",
            }).text === "LIVE",
        [liveScoreInfo, matchInfo],
    );

    useEffect(() => {
        if (!actualContestId) {
            navigate("/home");
            return;
        }

        let isActive = true;

        const fetchContestDetails = async () => {
            try {
                const contestRes = await axios.get(
                    `${CRICKET_API_URL}/api/cricket/contests/details/${actualContestId}`,
                    { headers },
                );

                if (isActive && contestRes.data?.success) {
                    setContestData(contestRes.data.data);
                    setMatchInfo((prev) => ({
                        ...prev,
                        matchId: prev?.matchId || contestRes.data.data?.matchId,
                    }));
                }
            } catch (error) {
                console.error("🚨 Error fetching contest details:", error);
            }
        };

        fetchContestDetails();

        return () => {
            isActive = false;
        };
    }, [actualContestId, navigate, headers]);

    useEffect(() => {
        if (!actualContestId || !actualMatchId) {
            return undefined;
        }

        let isActive = true;

        const fetchLiveData = async () => {
            try {
                const [lbRes, scoreRes] = await Promise.allSettled([
                    axios.get(`${CRICKET_API_URL}/api/cricket/contests/leaderboard/${actualContestId}`, { headers }),
                    axios.get(`${CRICKET_API_URL}/api/cricket/match/${actualMatchId}/live`, { headers }),
                ]);

                if (!isActive) return;

                if (lbRes.status === "fulfilled" && lbRes.value.data?.success) {
                    setLeaderboardData(sortLeaderboardEntries(lbRes.value.data.data || []));
                }

                if (scoreRes.status === "fulfilled" && scoreRes.value.data) {
                    const scoreData = scoreRes.value.data;
                    setLiveScoreInfo(scoreData);
                    setMatchInfo((prev) => ({
                        ...prev,
                        matchId: prev?.matchId || scoreData.matchId || actualMatchId,
                        status: scoreData.status || prev?.status,
                        apiStatus:
                            scoreData.apiStatus ||
                            scoreData.event_status ||
                            prev?.apiStatus,
                        status_info: scoreData.status_info || prev?.status_info,
                        event_status_info:
                            scoreData.event_status_info ||
                            scoreData.status_info ||
                            prev?.event_status_info,
                        event_home_final_result:
                            scoreData.event_home_final_result ||
                            prev?.event_home_final_result,
                        event_service_home:
                            scoreData.event_service_home ||
                            prev?.event_service_home,
                        event_away_final_result:
                            scoreData.event_away_final_result ||
                            prev?.event_away_final_result,
                        event_service_away:
                            scoreData.event_service_away ||
                            prev?.event_service_away,
                        toss: scoreData.toss || scoreData.event_toss || prev?.toss,
                        team1: {
                            ...(prev?.team1 || {}),
                            name: scoreData.score?.home?.name || prev?.team1?.name,
                            code: prev?.team1?.code || getTeamCode(scoreData.score?.home?.name),
                            image: prev?.team1?.image || DEFAULT_TEAM_IMAGE,
                        },
                        team2: {
                            ...(prev?.team2 || {}),
                            name: scoreData.score?.away?.name || prev?.team2?.name,
                            code: prev?.team2?.code || getTeamCode(scoreData.score?.away?.name),
                            image: prev?.team2?.image || DEFAULT_TEAM_IMAGE,
                        },
                    }));
                }
            } catch (error) {
                console.error("🚨 Error fetching live data:", error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        fetchLiveData();
        const interval = setInterval(() => {
            fetchLiveData();
        }, 5000);

        return () => {
            isActive = false;
            clearInterval(interval);
        };
    }, [actualContestId, actualMatchId, headers]);

    useEffect(() => {
        if (activeTab !== "commentary" || !actualMatchId || !isLiveMatch) return undefined;

        let isActive = true;

        const fetchCommentary = async () => {
            try {
                const res = await axios.get(
                    `${CRICKET_API_URL}/api/cricket/match/${actualMatchId}/commentary`,
                    { headers },
                );
                const nextCommentary = Array.isArray(res.data)
                    ? res.data
                    : res.data?.commentary || [];

                if (isActive) {
                    setCommentaryData(normalizeCommentaryItems(nextCommentary));
                }
            } catch (error) {
                console.log("Commentary fetch error");
            }
        };

        fetchCommentary();
        const interval = setInterval(fetchCommentary, 5000);

        return () => {
            isActive = false;
            clearInterval(interval);
        };
    }, [activeTab, actualMatchId, headers, isLiveMatch]);

    const handleOpponentClick = async (user) => {
        const resolvedTeamId = getResolvedTeamId(user);

        if (!resolvedTeamId) {
            alert("Team ID missing. Backend me userTeamId add karein!");
            return;
        }

        setSelectedOpponent(user);
        setOpponentLoading(true);
        setOpponentTeamData(null);

        try {
            const teamRes = await axios.get(`${CRICKET_API_URL}/api/cricket/team-details/${resolvedTeamId}`, {
                headers,
            });
            if (teamRes.data) {
                setOpponentTeamData(teamRes.data);
            }
        } catch (error) {
            console.error("Failed to load opponent team", error);
        } finally {
            setOpponentLoading(false);
        }
    };

    const prizeBreakup = contestData?.prizeBreakup || [];
    const homeTeam = liveScoreInfo?.score?.home?.name || matchInfo?.team1?.name || matchInfo?.teamA || "Team 1";
    const awayTeam = liveScoreInfo?.score?.away?.name || matchInfo?.team2?.name || matchInfo?.teamB || "Team 2";
    const homeCode = matchInfo?.team1?.code || getTeamCode(homeTeam);
    const awayCode = matchInfo?.team2?.code || getTeamCode(awayTeam);
    const headerMatch = useMemo(
        () => ({
            ...matchInfo,
            status:
                liveScoreInfo?.status ||
                matchInfo?.status ||
                "",
            apiStatus:
                liveScoreInfo?.apiStatus ||
                matchInfo?.apiStatus ||
                liveScoreInfo?.event_status ||
                "",
            status_info:
                liveScoreInfo?.status_info ||
                liveScoreInfo?.event_status_info ||
                matchInfo?.status_info ||
                matchInfo?.event_status_info ||
                "",
            event_status_info:
                liveScoreInfo?.event_status_info ||
                matchInfo?.event_status_info ||
                "",
            toss:
                liveScoreInfo?.toss ||
                liveScoreInfo?.event_toss ||
                matchInfo?.toss ||
                matchInfo?.event_toss ||
                "",
            isLive:
                liveScoreInfo?.isLive === true ||
                matchInfo?.isLive === true ||
                String(liveScoreInfo?.event_live || matchInfo?.event_live || "").trim() === "1",
            score: {
                home: {
                    ...matchInfo?.score?.home,
                    ...liveScoreInfo?.score?.home,
                    runs:
                        liveScoreInfo?.score?.home?.runs ??
                        matchInfo?.score?.home?.runs ??
                        "-",
                },
                away: {
                    ...matchInfo?.score?.away,
                    ...liveScoreInfo?.score?.away,
                    runs:
                        liveScoreInfo?.score?.away?.runs ??
                        matchInfo?.score?.away?.runs ??
                        "-",
                },
                overs:
                    liveScoreInfo?.score?.overs ??
                    matchInfo?.score?.overs ??
                    "",
            },
            event_home_final_result:
                liveScoreInfo?.event_home_final_result ||
                matchInfo?.event_home_final_result ||
                "",
            event_service_home:
                liveScoreInfo?.event_service_home ||
                matchInfo?.event_service_home ||
                "",
            event_away_final_result:
                liveScoreInfo?.event_away_final_result ||
                matchInfo?.event_away_final_result ||
                "",
            event_service_away:
                liveScoreInfo?.event_service_away ||
                matchInfo?.event_service_away ||
                "",
        }),
        [liveScoreInfo, matchInfo],
    );
    const status = getMatchStatus(headerMatch);
    const isCancelledMatch = status.isCancelled;
    const displayHomeScore = getTeamScore(headerMatch, "home");
    const displayAwayScore = getTeamScore(headerMatch, "away");
    const headerStatusText = status.info || "Match not started";
    const hasHeaderMatchData = Boolean(
        actualMatchId &&
        (
            headerMatch?.score?.home?.name ||
            headerMatch?.score?.away?.name ||
            matchInfo?.team1?.name ||
            matchInfo?.team2?.name ||
            matchInfo?.teamA ||
            matchInfo?.teamB ||
            matchInfo?.name
        ),
    );
    const isHeaderLoading = loading || !hasHeaderMatchData;
    const canShowWinnings = shouldShowLeaderboardWinnings({
        status: liveScoreInfo?.status || matchInfo?.status,
        settled: matchInfo?.settled || contestData?.settled,
    });

    useEffect(() => {
        if (!isLiveMatch && activeTab === "commentary") {
            setActiveTab("leaderboard");
        }
    }, [activeTab, isLiveMatch]);

    const resolvedLeaderboardData = useMemo(() => {
        const livePlayerPointsAvailable = livePlayerPointsLookup.size > 0;
        const nextEntries = (leaderboardData || []).map((entry) => {
            if (!livePlayerPointsAvailable || !entry?.userTeamId?.players?.length) {
                return entry;
            }

            const realTotalPoints = getResolvedTeamTotalPoints(
                entry.userTeamId,
                livePlayerPointsLookup,
            );

            return {
                ...entry,
                points: realTotalPoints,
                realTotalPoints,
            };
        });

        return rankLeaderboardEntries(nextEntries);
    }, [leaderboardData, livePlayerPointsLookup]);
    const resolvedSelectedOpponent = useMemo(() => {
        if (!selectedOpponent) return null;

        return (
            resolvedLeaderboardData.find(
                (entry) =>
                    String(entry?.userId || "") === String(selectedOpponent?.userId || "") &&
                    String(entry?.teamName || "") === String(selectedOpponent?.teamName || ""),
            ) || selectedOpponent
        );
    }, [resolvedLeaderboardData, selectedOpponent]);


    // --- 🔴 OPPONENT TEAM PREVIEW MODAL UI ---
    const TeamPreviewModal = () => {
        if (!selectedOpponent) return null;

        const groupedPlayers = (opponentTeamData?.players || []).reduce(
            (groups, player) => {
                const groupKey = getPlayerRoleGroupKey(player?.role);
                groups[groupKey].push(player);
                return groups;
            },
            {
                wicketKeepers: [],
                batters: [],
                allRounders: [],
                bowlers: [],
            },
        );

        const opponentTotalPoints = opponentTeamData?.players?.length
            ? calculateTeamPoints(
                opponentTeamData.players,
                livePlayerPointsLookup,
                opponentTeamData?.captainId,
                opponentTeamData?.viceCaptainId || opponentTeamData?.vcId,
            )
            : resolvedSelectedOpponent?.realTotalPoints ?? resolvedSelectedOpponent?.points;

        return (
            <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
                        <div className="modal-header text-white" style={{ backgroundColor: "#800000" }}>
                            <h6 className="modal-title mb-0 fw-bold">
                                {resolvedSelectedOpponent?.userName || selectedOpponent.userName}'s Team ({resolvedSelectedOpponent?.teamName || selectedOpponent.teamName})
                            </h6>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setSelectedOpponent(null)}></button>
                        </div>

                        <div
                            className="modal-body p-0"
                            style={{
                                background: "linear-gradient(180deg, #2e8b57 0%, #1a5233 100%)",
                                minHeight: "550px",
                                overflowY: "auto",
                            }}
                        >
                            {opponentLoading ? (
                                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "550px" }}>
                                    <div className="spinner-border text-light"></div>
                                </div>
                            ) : opponentTeamData && opponentTeamData.players ? (
                                <div className="h-100 d-flex flex-column">
                                    {/* Summary Banner */}
                                    <div
                                        className="text-white d-flex justify-content-around py-2"
                                        style={{ fontSize: "12px", backgroundColor: "rgba(0, 0, 0, 0.28)", backdropFilter: "blur(2px)" }}
                                    >
                                        <span>Total Points: <b className="text-warning">{formatPointsDisplay(opponentTotalPoints)}</b></span>
                                        <span>Rank: <b className="text-success">#{resolvedSelectedOpponent?.rank}</b></span>
                                    </div>

                                    <TeamPreviewField
                                        className="flex-grow-1"
                                        groupedPlayers={groupedPlayers}
                                        captainId={opponentTeamData?.captainId}
                                        viceCaptainId={opponentTeamData?.viceCaptainId || opponentTeamData?.vcId}
                                        isCaptainPlayer={(player) => matchesPlayerSelection(player, opponentTeamData?.captainId)}
                                        isViceCaptainPlayer={(player) =>
                                            matchesPlayerSelection(
                                                player,
                                                opponentTeamData?.viceCaptainId || opponentTeamData?.vcId,
                                            )
                                        }
                                        getPlayerMetaText={(player) =>
                                            `${formatPointsDisplay(
                                                getCalculatedPlayerPoints(
                                                    player,
                                                    livePlayerPointsLookup,
                                                    opponentTeamData?.captainId,
                                                    opponentTeamData?.viceCaptainId || opponentTeamData?.vcId,
                                                ),
                                            )} pts`
                                        }
                                    />
                                </div>
                            ) : (
                                <div className="d-flex align-items-center justify-content-center text-center text-white-50" style={{ minHeight: "550px" }}>
                                    Team data not found.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}>

            {/* 🔴 HEADER (LIVE MATCH SECTION) */}
            <div className="sticky-top shadow-sm" style={{ zIndex: 1000 }}>
                {/* Premium Gradient Header */}
                <div className="position-relative" style={{ background: "linear-gradient(135deg, #800000 0%, #4a0000 100%)", color: "white", overflow: "hidden" }}>

                    {/* Subtle Background Pattern/Glow (Optional purely for UI) */}
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)", pointerEvents: "none" }}></div>

                    {/* Top Navbar */}
                    <div className="d-flex align-items-center px-3 pt-3 pb-2 position-relative">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{ width: "32px", height: "32px", background: "rgba(255,255,255,0.15)", cursor: "pointer", transition: "all 0.2s" }}
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left fs-5 text-white"></i>
                        </div>

                        <div className="m-auto text-center "> {/* pe-4 to balance the back button */}
                            {isHeaderLoading ? (
                                <div
                                    className="rounded-pill mx-auto mb-1"
                                    style={{
                                        width: "102px",
                                        height: "32px",
                                        background: "rgba(255,255,255,0.22)",
                                        border: "1px solid rgba(255,255,255,0.18)",
                                        animation: "headerPulse 1.5s infinite ease-in-out",
                                    }}
                                ></div>
                            ) : (
                                <span
                                    className={`badge rounded-pill text-center px-3 py-2 mb-1 shadow-sm ${status.bg}`}
                                    style={{
                                        ...(status.text === "LIVE" ? { animation: "blink 1.5s infinite" } : {}),
                                        fontSize: "10px",
                                        letterSpacing: "0.5px",
                                        border: "1px solid rgba(255,255,255,0.2)"
                                    }}
                                >
                                    {status.text === "LIVE" ? <span className="text-white me-1">●</span> : null}
                                    {status.text}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="px-3 pb-3 position-relative">
                        {isHeaderLoading ? (
                            <div
                                className="rounded-4 p-3"
                                style={{
                                    background: "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.1) 100%)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.12)",
                                    animation: "headerPulse 1.5s infinite ease-in-out",
                                }}
                            >
                                <div className="d-none d-md-flex justify-content-center align-items-center gap-5 mt-2">
                                    {[0, 1].map((index) => (
                                        <div key={index} className="d-flex flex-column align-items-center">
                                            <div className="rounded-circle mb-2" style={{ width: "64px", height: "64px", background: "rgba(255,255,255,0.28)" }}></div>
                                            <div className="rounded-pill mb-2" style={{ width: "120px", height: "14px", background: "rgba(255,255,255,0.28)" }}></div>
                                            <div className="rounded-pill" style={{ width: "96px", height: "22px", background: "rgba(255,255,255,0.28)" }}></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="d-flex d-md-none flex-column position-relative mx-auto mt-3 mb-2" style={{ maxWidth: "380px", gap: "12px" }}>
                                    {[0, 1].map((index) => (
                                        <div key={index} className="d-flex justify-content-between align-items-center p-3 rounded-4" style={{ background: "rgba(255,255,255,0.18)" }}>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="rounded-circle" style={{ width: "38px", height: "38px", background: "rgba(255,255,255,0.28)" }}></div>
                                                <div className="rounded-pill" style={{ width: "90px", height: "12px", background: "rgba(255,255,255,0.28)" }}></div>
                                            </div>
                                            <div className="rounded-pill" style={{ width: "78px", height: "16px", background: "rgba(255,255,255,0.28)" }}></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div
                                style={isCancelledMatch ? {
                                    background: "#f8f9fa",
                                    border: "1px solid #d6d9dc",
                                    borderRadius: "20px",
                                    boxShadow: "0 10px 24px rgba(108, 117, 125, 0.18)",
                                    padding: "8px 10px 10px",
                                } : undefined}
                            >
                                {/* Desktop Scoreboard (d-md-flex) */}
                                <div className="d-none d-md-flex justify-content-center align-items-center gap-5 mt-2">
                                    {/* Home Team */}
                                    <div className="text-center d-flex flex-column align-items-center">
                                        <div
                                            className="rounded-circle p-1 mb-2 shadow-sm"
                                            style={{
                                                background: isCancelledMatch ? "#ffffff" : "rgba(255,255,255,0.1)",
                                                border: isCancelledMatch ? "2px solid #d6d9dc" : "2px solid rgba(255,255,255,0.2)",
                                            }}
                                        >
                                            <img
                                                src={matchInfo?.team1?.image || DEFAULT_TEAM_IMAGE}
                                                alt={homeCode}
                                                width="64"
                                                height="64"
                                                className="rounded-circle bg-white object-fit-cover"
                                            />
                                        </div>
                                        <h6 className="fw-bold mb-2" style={{ letterSpacing: "0.5px", color: isCancelledMatch ? "#1f2937" : "#ffffff" }}>{homeTeam}</h6>
                                        <div
                                            className="fw-bold px-4 py-1 rounded-pill"
                                            style={{
                                                backgroundColor: isCancelledMatch ? "#ffffff" : "rgba(0,0,0,0.25)",
                                                fontSize: "22px",
                                                color: isCancelledMatch ? "#4b5563" : "#ffd700",
                                                border: isCancelledMatch ? "1px solid #d6d9dc" : "1px solid rgba(255,215,0,0.3)",
                                            }}
                                        >
                                            {displayHomeScore}
                                        </div>
                                    </div>

                                    {/* VS Element */}
                                    <div className="d-flex flex-column align-items-center opacity-75">
                                        <div
                                            className="badge rounded-circle shadow d-flex align-items-center justify-content-center mb-1"
                                            style={{
                                                width: "36px",
                                                height: "36px",
                                                fontSize: "12px",
                                                background: isCancelledMatch ? "#e5e7eb" : "#ffffff",
                                                color: isCancelledMatch ? "#4b5563" : "#212529",
                                                border: isCancelledMatch ? "2px solid #d1d5db" : "2px solid #e0e0e0",
                                            }}
                                        >
                                            VS
                                        </div>
                                    </div>

                                    {/* Away Team */}
                                    <div className="text-center d-flex flex-column align-items-center">
                                        <div
                                            className="rounded-circle p-1 mb-2 shadow-sm"
                                            style={{
                                                background: isCancelledMatch ? "#ffffff" : "rgba(255,255,255,0.1)",
                                                border: isCancelledMatch ? "2px solid #d6d9dc" : "2px solid rgba(255,255,255,0.2)",
                                            }}
                                        >
                                            <img
                                                src={matchInfo?.team2?.image || DEFAULT_TEAM_IMAGE}
                                                alt={awayCode}
                                                width="64"
                                                height="64"
                                                className="rounded-circle bg-white object-fit-cover"
                                            />
                                        </div>
                                        <h6 className="fw-bold mb-2" style={{ letterSpacing: "0.5px", color: isCancelledMatch ? "#1f2937" : "#ffffff" }}>{awayTeam}</h6>
                                        <div
                                            className="fw-bold px-4 py-1 rounded-pill"
                                            style={{
                                                backgroundColor: isCancelledMatch ? "#ffffff" : "rgba(0,0,0,0.25)",
                                                fontSize: "22px",
                                                color: isCancelledMatch ? "#4b5563" : "#ffd700",
                                                border: isCancelledMatch ? "1px solid #d6d9dc" : "1px solid rgba(255,215,0,0.3)",
                                            }}
                                        >
                                            {displayAwayScore}
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile Scoreboard (d-md-none) */}
                                <div
                                    className="d-flex d-md-none flex-column position-relative mx-auto mt-3 mb-2"
                                    style={{
                                        maxWidth: "380px",
                                        background: isCancelledMatch ? "linear-gradient(145deg, #f8f9fa, #eef1f4)" : "#ffffff",
                                        borderRadius: "20px",
                                        boxShadow: isCancelledMatch ? "0 8px 24px rgba(108, 117, 125, 0.18)" : "0 8px 24px rgba(0, 0, 0, 0.08)",
                                        border: isCancelledMatch ? "1px solid #d6d9dc" : "1px solid rgba(0,0,0,0.04)",
                                    }}
                                >
                                    {/* Home Team Row */}
                                    <div className="d-flex justify-content-between align-items-center p-3 p-sm-4 position-relative" style={{ borderBottom: "1px dashed #e9ecef" }}>
                                        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                            <div className="rounded-circle p-1 bg-white shadow-sm flex-shrink-0" style={{ border: "2px solid #f8f9fa" }}>
                                                <div className="rounded-circle overflow-hidden bg-light" style={{ width: "38px", height: "38px" }}>
                                                    <img
                                                        src={matchInfo?.team1?.image || DEFAULT_TEAM_IMAGE}
                                                        alt={homeCode}
                                                        className="w-100 h-100 object-fit-cover"
                                                    />
                                                </div>
                                            </div>
                                            <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                <div className="fw-bolder text-dark text-truncate" style={{ fontSize: "15px" }}>{homeTeam}</div>
                                            </div>
                                        </div>

                                <div
                                    className="d-flex align-items-center justify-content-center px-3 py-2 ms-2 flex-shrink-0 rounded-3 shadow-sm"
                                    style={{
                                        background: isCancelledMatch ? "#ffffff" : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                                                border: isCancelledMatch ? "1px solid #d6d9dc" : "1px solid #e9ecef",
                                                minWidth: "65px",
                                            }}
                                        >
                                            <span className="fw-bold fs-5" style={{ color: isCancelledMatch ? "#4b5563" : "#800000", lineHeight: "1" }}>{displayHomeScore}</span>
                                        </div>
                                    </div>

                                    {/* Center Floating VS Badge */}
                                    <div className="position-absolute start-50 translate-middle" style={{ top: "50%", zIndex: 10 }}>
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                            style={{
                                                width: "34px",
                                                height: "34px",
                                                background: isCancelledMatch ? "#e5e7eb" : "#ffffff",
                                                border: isCancelledMatch ? "1px solid #d1d5db" : "1px solid #e9ecef",
                                            }}
                                        >
                                            <span className="fw-bolder text-muted" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>VS</span>
                                        </div>
                                    </div>

                                    {/* Away Team Row */}
                                    <div className="d-flex justify-content-between align-items-center p-3 p-sm-4 position-relative">
                                        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                            <div className="rounded-circle p-1 bg-white shadow-sm flex-shrink-0" style={{ border: "2px solid #f8f9fa" }}>
                                                <div className="rounded-circle overflow-hidden bg-light" style={{ width: "38px", height: "38px" }}>
                                                    <img
                                                        src={matchInfo?.team2?.image || DEFAULT_TEAM_IMAGE}
                                                        alt={awayCode}
                                                        className="w-100 h-100 object-fit-cover"
                                                    />
                                                </div>
                                            </div>
                                            <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                <div className="fw-bolder text-dark text-truncate" style={{ fontSize: "15px" }}>{awayTeam}</div>
                                            </div>
                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-center px-3 py-2 ms-2 flex-shrink-0 rounded-3 shadow-sm"
                                            style={{
                                                background: isCancelledMatch ? "#ffffff" : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                                                border: isCancelledMatch ? "1px solid #d6d9dc" : "1px solid #e9ecef",
                                                minWidth: "65px",
                                            }}
                                        >
                                            <span className="fw-bold fs-5" style={{ color: isCancelledMatch ? "#4b5563" : "#800000", lineHeight: "1" }}>{displayAwayScore}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center mt-3 px-2" style={{ color: isCancelledMatch ? "#6b7280" : "rgba(255,255,255,0.82)", fontSize: "12px", fontWeight: 600, letterSpacing: "0.2px" }}>
                                    {headerStatusText}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sleek Tab Navigation */}
                <div className="d-flex bg-white text-center border-bottom shadow-sm">
                    {["leaderboard", "prizePool", "commentary"].map((tab) => {
                        const isActive = activeTab === tab;
                        const isCommentaryTabDisabled = tab === "commentary" && !isLiveMatch;
                        return (
                            <div
                                key={tab}
                                className={`flex-grow-1 py-3 fw-bold text-uppercase position-relative d-flex justify-content-center align-items-center gap-1 ${isActive ? "text-dark" : "text-muted"} ${isCommentaryTabDisabled ? "opacity-50" : ""}`}
                                style={{
                                    fontSize: "12px",
                                    cursor: isCommentaryTabDisabled ? "not-allowed" : "pointer",
                                    transition: "all 0.3s ease",
                                    backgroundColor: isActive ? "#fffafb" : "transparent"
                                }}
                                onClick={() => {
                                    if (isCommentaryTabDisabled) return;
                                    setActiveTab(tab);
                                }}
                            >
                                {/* Tiny Icons for Premium feel */}
                                {tab === "leaderboard" && <i className={`bi bi-bar-chart-fill ${isActive ? "text-danger" : ""}`} style={{ fontSize: "14px" }}></i>}
                                {tab === "prizePool" && <i className={`bi bi-trophy-fill ${isActive ? "text-warning" : ""}`} style={{ fontSize: "14px" }}></i>}
                                {tab === "commentary" && <i className={`bi bi-mic-fill ${isActive ? "text-primary" : ""}`} style={{ fontSize: "14px" }}></i>}

                                <span className="d-none d-sm-inline ms-1">
                                    {tab === "leaderboard" ? "Leaderboard" : tab === "prizePool" ? "Prize Pool" : "Commentary"}
                                </span>
                                <span className="d-inline d-sm-none ms-1" style={{ fontSize: "11px" }}>
                                    {tab === "leaderboard" ? "Board" : tab === "prizePool" ? "Prizes" : "Live"}
                                </span>

                                {/* Animated Active Indicator */}
                                {isActive && (
                                    <div
                                        className="position-absolute bottom-0 start-50 translate-middle-x rounded-top"
                                        style={{ height: "4px", width: "60%", backgroundColor: "#800000", borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }}
                                    ></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="container-fluid p-0">
                {loading ? (
                    <div className="text-center py-5 mt-5"><div className="spinner-border text-danger"></div></div>
                ) : (
                    <>
                        {/* 🏆 LEADERBOARD TAB */}
                        {activeTab === "leaderboard" && (
                            <div>
                                <div className="d-flex px-3 py-2 bg-light text-muted fw-bold border-bottom" style={{ fontSize: "11px" }}>
                                    <div style={{ width: "15%" }}>RANK</div>
                                    <div style={{ width: "60%" }}>TEAM NAME</div>
                                    <div className="text-end" style={{ width: "25%" }}>POINTS</div>
                                </div>

                                {resolvedLeaderboardData.length > 0 ? resolvedLeaderboardData.map((user) => {
                                    const isMe = String(user.userId) === String(currentUserId);
                                    const winnings = normalizePoints(
                                        user?.wonAmount ??
                                        user?.winnings ??
                                        user?.prize,
                                    ) ?? 0;
                                    const displayUserName = user.userName || "User";
                                    const displayTeamName = user.teamName || "Team 1";
                                    const formattedWinnings = winnings.toLocaleString("en-IN");

                                    let rankStyle = {};
                                    if (user.rank === 1) rankStyle = { backgroundColor: "#fffbeb", borderLeft: "4px solid #fbbc04" };
                                    else if (user.rank === 2) rankStyle = { backgroundColor: "#f8f9fa", borderLeft: "4px solid #bdc3c7" };
                                    else if (user.rank === 3) rankStyle = { backgroundColor: "#fff6f0", borderLeft: "4px solid #cd7f32" };
                                    else rankStyle = { backgroundColor: "#ffffff" };

                                    if (isMe) rankStyle = { backgroundColor: "#f9e6e6", borderLeft: "4px solid #800000" };

                                    return (
                                        <div
                                            key={user.userId + user.teamName}
                                            className="d-flex align-items-center px-3 py-3 border-bottom shadow-sm"
                                            style={{ ...rankStyle, cursor: "pointer" }}
                                            onClick={() => handleOpponentClick(user)}
                                        >
                                            <div className="fw-bold text-dark" style={{ width: "15%", fontSize: "14px" }}>
                                                #{user.rank || "-"}
                                            </div>
                                            <div className="d-flex align-items-center" style={{ width: "60%" }}>
                                                <div className="rounded-circle d-flex justify-content-center align-items-center text-white bg-secondary me-2 overflow-hidden shadow-sm" style={{ width: "36px", height: "36px", fontSize: "14px" }}>
                                                    {user.userAvatar && !user.userAvatar.includes('icon512') ?
                                                        <img src={user.userAvatar} alt="DP" className="w-100 h-100 object-fit-cover" /> :
                                                        displayUserName.charAt(0).toUpperCase()
                                                    }
                                                </div>
                                                <div className="d-flex flex-column">
                                                    <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                                                        {displayUserName} {isMe && <span className="badge bg-danger ms-1" style={{ fontSize: "9px" }}>YOU</span>}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: "11px" }}>({displayTeamName})</div>
                                                    {canShowWinnings && winnings > 0 && (
                                                        <div
                                                            className="d-inline-flex align-items-center gap-1 mt-1 px-2 py-1 rounded-pill fw-bold align-self-start"
                                                            style={{
                                                                fontSize: "11px",
                                                                color: "#166534",
                                                                background: "linear-gradient(135deg, #fef9c3 0%, #dcfce7 100%)",
                                                                border: "1px solid rgba(34, 197, 94, 0.28)",
                                                                boxShadow: "0 6px 14px rgba(34, 197, 94, 0.16)",
                                                            }}
                                                        >
                                                            <span role="img" aria-label="Trophy">🏆</span>
                                                            <span>Won ₹{formattedWinnings}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="fw-black text-end text-dark" style={{ width: "25%", fontSize: "15px" }}>
                                                {formatPointsDisplay(user.points)}
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="text-center py-5 text-muted">No participants yet.</div>
                                )}
                            </div>
                        )}

                        {activeTab === "prizePool" && (
                            <div className="p-3">
                                <div
                                    className="card border-0 position-relative overflow-hidden mb-3"
                                    style={{
                                        borderRadius: "20px",
                                        background: "#ffffff",
                                        boxShadow: "0 8px 24px rgba(128, 0, 0, 0.08)", // Subtle maroon shadow
                                    }}
                                >
                                    {/* Total Prize Pool Header - Maroon Gradient */}
                                    <div
                                        className="card-header border-0 pt-4 pb-4 text-center position-relative"
                                        style={{
                                            background: "linear-gradient(135deg, #800000 0%, #4a0000 100%)",
                                            color: "white"
                                        }}
                                    >
                                        {/* Background Glass Pattern */}
                                        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)", pointerEvents: "none" }}></div>

                                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2 shadow" style={{ width: "48px", height: "48px", background: "rgba(255, 255, 255, 0.15)", border: "1px solid rgba(255, 255, 255, 0.3)" }}>
                                            <i className="bi bi-trophy-fill text-warning fs-4" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}></i>
                                        </div>
                                        <p className="text-white-50 fw-bold mb-1" style={{ fontSize: "11px", letterSpacing: "1.5px" }}>
                                            TOTAL PRIZE POOL
                                        </p>
                                        <h2 className="fw-black text-white mb-0" style={{ fontSize: "32px", letterSpacing: "-0.5px" }}>
                                            ₹{contestData?.prizePool || 0}
                                        </h2>
                                    </div>

                                    {/* Prize Breakdown Section */}
                                    <div className="card-body p-0">
                                        {/* Table Header */}
                                        <div className="d-flex px-4 py-2 text-muted fw-bolder" style={{ background: "#f8f9fa", fontSize: "11px", letterSpacing: "0.5px", borderBottom: "1px solid #e9ecef" }}>
                                            <div className="flex-grow-1">RANK</div>
                                            <div className="text-end">WINNINGS</div>
                                        </div>

                                        {/* Prize List */}
                                        {prizeBreakup.length > 0 ? prizeBreakup.map((prize, idx) => (
                                            <div
                                                key={idx}
                                                className="d-flex justify-content-between align-items-center px-4 py-3 position-relative"
                                                style={{
                                                    borderBottom: idx === prizeBreakup.length - 1 ? "none" : "1px solid #f1f3f5",
                                                    background: idx === 0 ? "#fffafb" : "transparent" // Very light maroon tint for 1st rank
                                                }}
                                            >
                                                {/* Accent line for 1st Rank */}
                                                {idx === 0 && <div className="position-absolute start-0 top-0 h-100" style={{ width: "4px", backgroundColor: "#800000" }}></div>}

                                                <div className="d-flex align-items-center gap-3">
                                                    {idx === 0 ? (
                                                        <div className="badge rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center shadow-sm" style={{ width: "26px", height: "26px", padding: 0, border: "2px solid #ffcc00" }}>
                                                            <i className="bi bi-star-fill" style={{ fontSize: "11px" }}></i>
                                                        </div>
                                                    ) : (
                                                        <div className="badge rounded-circle bg-light text-secondary d-flex align-items-center justify-content-center border" style={{ width: "26px", height: "26px", padding: 0 }}>
                                                            <span style={{ fontSize: "11px" }}>#</span>
                                                        </div>
                                                    )}
                                                    <div className={`fw-bolder ${idx === 0 ? "text-dark" : "text-secondary"}`} style={{ fontSize: "15px" }}>
                                                        {formatPrizeRank(prize)}
                                                    </div>
                                                </div>
                                                <div className="fw-black" style={{ fontSize: "16px", color: idx === 0 ? "#800000" : "#212529" }}>
                                                    ₹{prize.prizeAmount || prize.amount}
                                                </div>
                                            </div>
                                        )) : (
                                            /* Premium Empty State */
                                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                                <div className="rounded-circle d-flex justify-content-center align-items-center mb-3 shadow-sm" style={{ width: "64px", height: "64px", backgroundColor: "#fff5f5", border: "1px solid #ffe6e6" }}>
                                                    <i className="bi bi-wallet2 text-danger opacity-50" style={{ fontSize: "28px" }}></i>
                                                </div>
                                                <p className="text-muted fw-medium mb-0" style={{ fontSize: "14px" }}>
                                                    Prize breakdown not available yet.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "commentary" && isLiveMatch && (
                            <div>
                                <div className="px-3 py-2 bg-light border-bottom text-muted fw-medium" style={{ fontSize: "11px" }}>
                                    Real live commentary, latest ball first.
                                </div>
                                {commentaryData.length > 0 ? commentaryData.map((comm, idx) => {
                                    const runsMeta = getCommentaryRunsMeta(comm.runs);

                                    return (
                                        <div
                                            key={comm.id || idx}
                                            className="d-flex align-items-center px-3 py-3 bg-white shadow-sm mb-1"
                                            style={{ borderBottom: "1px solid #f1f3f5" }}
                                        >
                                            <div
                                                className="badge rounded-pill text-white me-3 d-flex justify-content-center align-items-center shadow-sm flex-shrink-0"
                                                style={{
                                                    backgroundColor: "#800000",
                                                    minWidth: "50px",
                                                    height: "28px",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                {comm.over || "-"}
                                            </div>

                                            <div className="d-flex flex-column">
                                                <div className="text-dark fw-medium" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                                                    {comm.text || comm.commentary || "Update..."}
                                                </div>
                                                {runsMeta && (
                                                    <div className={`fw-bold mt-1 ${runsMeta.className}`} style={{ fontSize: "13px" }}>
                                                        {runsMeta.label}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="text-center py-5 text-muted">No live commentary yet.</div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal Render */}
            <TeamPreviewModal />

            {/* Custom Styles */}
            <style>
                {`
                    @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
                    @keyframes headerPulse { 0% { opacity: 1; } 50% { opacity: 0.55; } 100% { opacity: 1; } }
                `}
            </style>
        </div>
    );
};

export default LiveLeaderboard;
